// The Classic layout's "Direct messages" section of the sidebar (most recently active first).
import { Link } from "react-router-dom";
import { dmTitle, type Channel } from "@bloom/shared";
import type { ChannelState } from "../../realtime.ts";
import { rowMenuHandlers, type OpenRowMenu } from "../ChannelRowMenu.tsx";
import { DmAvatar, shownDms } from "../DmList.tsx";
import { MutedIcon } from "../icons.tsx";

export function ClassicDmList(props: {
  serverId: string;
  dms: Channel[];
  me: string | null;
  names: Map<string, string>;
  online: Set<string>;
  activeId: string | undefined;
  read: Record<string, number>;
  /** Unread messages per DM (every message from someone else in a DM counts, for the red pill). */
  mentionCounts: Record<string, number>;
  byChannel: Record<string, ChannelState>;
  /** DMs I closed (snapshot dmClosed). */
  closed: Record<string, number>;
  onNavigate: () => void;
  onNew: () => void;
  /** The row's ×: close it until its next message. */
  onClose: (c: Channel) => void;
  /** A row's menu was asked for (long press, right-click, the Menu key). */
  onMenu?: OpenRowMenu;
  /** Conversations muted right now: faded, with a crossed-out bell and no unread dot (mention badges stay). */
  muted?: Set<string>;
  /** How the sidebar's unread pills count a row (Server.tsx). */
  unreadMark?: (id: string, unread: boolean, mentions: number) => string | undefined;
}) {
  const { serverId, dms, me, names, online, activeId, read, mentionCounts, byChannel, closed, onNavigate, onNew, onClose } = props;
  const shown = shownDms(dms, byChannel, activeId, closed);
  return (
    <section aria-label="Direct messages" className="dm-section">
      <div className="category-head">
        <span className="category-name" style={{ flex: 1 }}>Direct messages</span>
        <button className="icon-btn" onClick={onNew} aria-label="New direct message" title="New direct message">+</button>
      </div>
      {shown.map((c) => {
        const isMuted = !!props.muted?.has(c.id);
        const unread = c.lastSeq > (read[c.id] ?? 0) && c.id !== activeId;
        const title = dmTitle(c, me, names);
        return (
          <div key={c.id} className="dm-item">
            <Link
              to={`/s/${serverId}/${c.id}`}
              onClick={onNavigate}
              className={`channel dm-row${c.id === activeId ? " active" : ""}${unread && !isMuted ? " unread" : ""}${isMuted ? " notify-muted" : ""}`}
              aria-current={c.id === activeId ? "page" : undefined}
              title={title}
              data-unread={props.unreadMark?.(c.id, unread, unread ? (mentionCounts[c.id] ?? 0) : 0)}
              {...(props.onMenu ? rowMenuHandlers(props.onMenu, c.id) : {})}
            >
              <DmAvatar channel={c} me={me} names={names} size={20} online={online} />
              <span className="dm-name">{title}</span>
              {isMuted && <MutedIcon className="row-muted" />}
              {unread && (mentionCounts[c.id] ?? 0) > 0 ? (
                <span className="mention-pill" aria-label={`${mentionCounts[c.id]} unread`}>{mentionCounts[c.id]! > 99 ? "99+" : mentionCounts[c.id]}</span>
              ) : (
                unread && !isMuted && <span className="unread-dot" aria-label="unread" />
              )}
            </Link>
            {/* Beside the link, not in it: a button inside a link would open the DM too. */}
            <button type="button" className="icon-btn dm-close" onClick={() => onClose(c)} aria-label={`Close conversation with ${title}`} title="Close">×</button>
          </div>
        );
      })}
    </section>
  );
}
